<template>
  <div class="mobile-tree-filter">
    <div
      v-for="node in treeNodes"
      :key="node.key"
      class="mobile-tree-node"
      :style="{ paddingLeft: `${node.level * 20}px` }"
    >
      <div
        class="mobile-tree-node-content"
        :class="{ 'has-children': node.children && node.children.length > 0 }"
        @click="handleNodeClick(node)"
      >
        <!-- 展开/收起按钮 -->
        <div
          v-if="node.children && node.children.length > 0"
          class="mobile-tree-switcher"
          @click.stop="toggleExpand(node)"
        >
          <ChevronRight
            :size="18"
            :class="['mobile-tree-switcher-icon', { 'mobile-tree-switcher-icon--open': node.expanded }]"
          />
        </div>
        <div v-else class="mobile-tree-switcher-placeholder"></div>

        <!-- 复选框 -->
        <div
          class="mobile-tree-checkbox"
          :class="{
            'mobile-tree-checkbox--checked': node.checked,
            'mobile-tree-checkbox--indeterminate': node.indeterminate
          }"
          @click.stop="toggleCheck(node)"
        >
          <Check v-if="node.checked" :size="12" class="mobile-tree-checkbox-icon" />
          <Minus v-else-if="node.indeterminate" :size="12" class="mobile-tree-checkbox-icon" />
        </div>

        <!-- 节点标题 -->
        <div class="mobile-tree-title">
          {{ node.title }}
        </div>
      </div>

      <!-- 子节点 -->
      <transition name="mobile-tree-slide">
        <div v-if="node.expanded && node.children && node.children.length > 0" class="mobile-tree-children">
          <MobileTreeFilter
            :nodes="node.children"
            :selected-keys="selectedKeys"
            :level="node.level + 1"
            @update:selected-keys="$emit('update:selectedKeys', $event)"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ChevronRight, Check, Minus } from 'lucide-vue-next'

export default {
  name: 'MobileTreeFilter',
  components: {
    ChevronRight,
    Check,
    Minus
  },
  props: {
    nodes: {
      type: Array,
      required: true
    },
    selectedKeys: {
      type: Array,
      default: () => []
    },
    level: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:selectedKeys'],
  data() {
    return {
      expandedNodes: new Set()
    }
  },
  computed: {
    treeNodes() {
      return this.nodes.map(node => {
        const nodeKey = String(node.key || node.value || '')
        const selectedKeyStrings = this.selectedKeys.map(k => String(k))
        return {
          ...node,
          key: nodeKey,
          level: this.level,
          expanded: this.expandedNodes.has(nodeKey),
          checked: selectedKeyStrings.includes(nodeKey),
          indeterminate: this.isIndeterminate(node)
        }
      })
    }
  },
  methods: {
    toggleExpand(node) {
      const nodeKey = String(node.key || node.value)
      if (this.expandedNodes.has(nodeKey)) {
        this.expandedNodes.delete(nodeKey)
      } else {
        this.expandedNodes.add(nodeKey)
      }
      this.$forceUpdate()
    },
    toggleCheck(node) {
      const currentKeys = [...this.selectedKeys].map(k => String(k))
      const nodeKey = String(node.key || node.value || '')
      const isChecked = currentKeys.includes(nodeKey)

      if (isChecked) {
        // 取消选中：移除当前节点
        this.$emit('update:selectedKeys', currentKeys.filter(key => String(key) !== nodeKey))
      } else {
        // 选中：添加当前节点（check-strictly 模式，不自动选中子节点）
        this.$emit('update:selectedKeys', [...currentKeys, nodeKey])
      }
    },
    handleNodeClick(node) {
      // 点击节点内容区域，切换复选框状态
      this.toggleCheck(node)
    },
    getAllDescendantKeys(node) {
      const nodeKey = String(node.key || node.value)
      let keys = [nodeKey]
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          keys = keys.concat(this.getAllDescendantKeys(child))
        })
      }
      return keys
    },
    isIndeterminate(node) {
      // 检查是否有部分子节点被选中（用于显示半选状态）
      if (!node.children || node.children.length === 0) {
        return false
      }
      const selectedKeyStrings = this.selectedKeys.map(k => String(k))
      const childKeys = node.children.map(child => String(child.key || child.value || ''))
      const selectedChildCount = childKeys.filter(key => selectedKeyStrings.includes(key)).length
      return selectedChildCount > 0 && selectedChildCount < childKeys.length
    }
  }
}
</script>

<style scoped>
.mobile-tree-filter {
  width: 100%;
}

.mobile-tree-node {
  width: 100%;
  margin-bottom: 10px;
}

.mobile-tree-node:last-child {
  margin-bottom: 0;
}

.mobile-tree-node-content {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 16px;
  margin: 0;
  border-radius: 6px;
  background: #fff;
  transition: all 0.2s;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  user-select: none;
  -webkit-user-select: none;
  position: relative;
}

.mobile-tree-node-content:active {
  background: #f5f5f5;
}

.mobile-tree-node-content.has-children {
  padding-left: 8px;
}

.mobile-tree-switcher {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
}

.mobile-tree-switcher-placeholder {
  width: 36px;
  height: 36px;
  margin-right: 8px;
  flex-shrink: 0;
}

.mobile-tree-switcher-icon {
  color: rgba(0, 0, 0, 0.45);
  transition: transform 0.3s;
}

.mobile-tree-switcher-icon--open {
  transform: rotate(90deg);
}

.mobile-tree-checkbox {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border: 2px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
}

.mobile-tree-checkbox--checked {
  background: #1989fa;
  border-color: #1989fa;
}

.mobile-tree-checkbox--indeterminate {
  background: #1989fa;
  border-color: #1989fa;
}

.mobile-tree-checkbox-icon {
  color: #fff;
  stroke-width: 3;
}

.mobile-tree-title {
  flex: 1;
  color: rgba(0, 0, 0, 0.85);
  font-size: 15px;
  line-height: 36px;
  word-break: break-word;
  padding-right: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.mobile-tree-children {
  overflow: hidden;
}

/* 展开/收起动画 */
.mobile-tree-slide-enter-active,
.mobile-tree-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
}

.mobile-tree-slide-enter-from,
.mobile-tree-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
